import { PontBascule, ScaleAgentConfig } from '../types/scaleAgent';
import { buildApiUrl } from '../config/api';

/**
 * Service de gestion des ponts bascule et de leurs configurations
 * Gère la récupération et la configuration des Scale Agents par site
 */
export class PontBasculeService {
  private static instance: PontBasculeService;
  private pontsBascule: Map<string, PontBascule> = new Map();
  private sitePontsBascule: Map<string, PontBascule[]> = new Map();

  private constructor() {}

  static getInstance(): PontBasculeService {
    if (!PontBasculeService.instance) {
      PontBasculeService.instance = new PontBasculeService();
    }
    return PontBasculeService.instance;
  }

  /**
   * Obtenir les headers d'authentification
   */
  private getAuthHeaders(): HeadersInit {
    // Vérifier toutes les clés possibles
    const accessToken = localStorage.getItem('access_token');
    const authToken = localStorage.getItem('auth_token');
    const sessionToken = sessionStorage.getItem('access_token');
    
    // Priorité : access_token > auth_token > sessionStorage
    const token = accessToken || authToken || sessionToken;
    
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
      
    } else {
      console.warn('⚠️ Aucun token trouvé dans le storage');
    }
    
    return headers;
  }

  /**
   * Récupérer tous les ponts bascule
   */
  async getAllPontsBascule(): Promise<PontBascule[]> {
    try {
      // D'abord, récupérer la liste de tous les ponts
      const response = await fetch(buildApiUrl('/pont-bascule/ponts-bascule/'), {
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentification requise. Veuillez vous connecter.');
        }
        throw new Error('Erreur lors de la récupération des ponts bascule');
      }
      
      const data = await response.json();
      const pontsBascule = data.results || data;
      
      if (!Array.isArray(pontsBascule) || pontsBascule.length === 0) {
        return [];
      }
      
      // Pour chaque pont, récupérer les détails complets via l'endpoint de détail
     
      const pontsWithDetails = await Promise.all(
        pontsBascule.map(async (pont) => {
          try {
            // Utiliser l'endpoint de détail pour obtenir toutes les informations
            const detailResponse = await fetch(buildApiUrl(`/pont-bascule/ponts-bascule/${pont.id}/`), {
              headers: this.getAuthHeaders(),
            });
            
            if (detailResponse.ok) {
              const pontDetail = await detailResponse.json();
              return this.mapBackendToFrontend(pontDetail);
            } else {
              return this.mapBackendToFrontend(pont);
            }
          } catch (detailError) {
            console.warn(`⚠️ Erreur lors de la récupération des détails du pont ${pont.id}:`, detailError);
            return this.mapBackendToFrontend(pont);
          }
        })
      );
      // Mettre à jour le cache local
      this.updateCache(pontsWithDetails);
      
      return pontsWithDetails;
    } catch (error) {
      console.error('Erreur récupération tous les ponts bascule:', error);
      // Retourner les données en cache si disponibles
      return Array.from(this.pontsBascule.values());
    }
  }

  /**
   * Récupérer les ponts bascule d'un site spécifique avec données complètes
   * Utilise l'endpoint de détail pour chaque pont pour garantir la cohérence
   */
  async getPontsBasculeBySite(siteId: string): Promise<PontBascule[]> {
    try {
      // D'abord, récupérer la liste des ponts du site
      const response = await fetch(buildApiUrl(`/pont-bascule/ponts-bascule/by_site/?site_id=${siteId}`), {
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentification requise. Veuillez vous connecter.');
        }
        throw new Error('Erreur lors de la récupération des ponts bascule du site');
      }
      
      const data = await response.json();
      const pontsBascule = data.results || data;
      
      if (!Array.isArray(pontsBascule) || pontsBascule.length === 0) {
        return [];
      }
      
      // Pour chaque pont, récupérer les détails complets via l'endpoint de détail
     
      
      const pontsWithDetails = await Promise.all(
        pontsBascule.map(async (pont) => {
          try {
            // Utiliser l'endpoint de détail pour obtenir toutes les informations
            const detailResponse = await fetch(buildApiUrl(`/pont-bascule/ponts-bascule/${pont.id}/`), {
              headers: this.getAuthHeaders(),
            });
            
            if (detailResponse.ok) {
              const pontDetail = await detailResponse.json();
             
              return this.mapBackendToFrontend(pontDetail);
            } else {
             
              return this.mapBackendToFrontend(pont);
            }
          } catch (detailError) {
            console.warn(`⚠️ Erreur lors de la récupération des détails du pont ${pont.id}:`, detailError);
            return this.mapBackendToFrontend(pont);
          }
        })
      );
      
      
      
      // Mettre à jour le cache local
      this.updateCache(pontsWithDetails);
      
      return pontsWithDetails;
    } catch (error) {
      console.error('Erreur récupération ponts bascule par site:', error);
      // Retourner les données en cache si disponibles
      return this.sitePontsBascule.get(siteId) || [];
    }
  }

  /**
   * Récupérer un pont bascule par ID avec données complètes
   * Utilise l'endpoint de détail pour garantir la cohérence
   */
  async getPontBasculeById(id: string): Promise<PontBascule | null> {
    try {
      
      
      const response = await fetch(buildApiUrl(`/pont-bascule/ponts-bascule/${id}/`), {
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentification requise. Veuillez vous connecter.');
        }
        throw new Error('Erreur lors de la récupération du pont bascule');
      }
      
      const pontBascule = await response.json();
      
      
      // Mapper les données du backend vers le frontend
      const mappedPontBascule = this.mapBackendToFrontend(pontBascule);
      
      // Mettre à jour le cache local
      this.pontsBascule.set(id, mappedPontBascule);
      
      return mappedPontBascule;
    } catch (error) {
      console.error('Erreur récupération pont bascule par ID:', error);
      // Retourner les données en cache si disponibles
      return this.pontsBascule.get(id) || null;
    }
  }

  /**
   * Récupérer la configuration du Scale Agent pour un pont bascule
   */
  async getScaleAgentConfig(pontBasculeId: string): Promise<ScaleAgentConfig | null> {
    try {
      const pontBascule = await this.getPontBasculeById(pontBasculeId);
      if (!pontBascule) {
        return null;
      }

      return pontBascule.scaleAgentConfig || null;
    } catch (error) {
      console.error('Erreur récupération config Scale Agent:', error);
      return null;
    }
  }

  /**
   * Mettre à jour la configuration d'un pont bascule
   */
  async updatePontBascule(id: string, updates: Partial<PontBascule>): Promise<PontBascule> {
    try {
      // Mapper les données du frontend vers le format attendu par le backend
      const mappedUpdates = this.mapFrontendToBackend(updates as any);
      
      const response = await fetch(buildApiUrl(`/pont-bascule/ponts-bascule/${id}/`), {
        method: 'PATCH',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(mappedUpdates),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentification requise. Veuillez vous connecter.');
        }
        
        // Récupérer les détails de l'erreur
        let errorMessage = 'Erreur lors de la mise à jour du pont bascule';
        try {
          const errorData = await response.json();
          
          // Construire un message d'erreur détaillé
          const errorDetails: string[] = [];
          
          if (errorData.detail) {
            errorDetails.push(errorData.detail);
          }
          
          if (errorData.message) {
            errorDetails.push(errorData.message);
          }
          
          if (typeof errorData === 'string') {
            errorDetails.push(errorData);
          }
          
          if (errorData.non_field_errors) {
            errorDetails.push(errorData.non_field_errors.join(', '));
          }
          
          if (errorData.error) {
            errorDetails.push(errorData.error);
          }
          
          // Traiter les erreurs de champs spécifiques
          for (const [field, errors] of Object.entries(errorData)) {
            if (Array.isArray(errors) && errors.length > 0) {
              errorDetails.push(`${field}: ${errors.join(', ')}`);
            }
          }
          
          if (errorDetails.length > 0) {
            errorMessage = `Erreur 400: ${errorDetails.join(' | ')}`;
          }
        } catch (parseError) {
          console.error('Impossible de parser la réponse d\'erreur:', parseError);
        }
        
        throw new Error(errorMessage);
      }

      const updatedPontBascule = await response.json();
      
      // Mapper les données du backend vers le frontend
      const mappedPontBascule = this.mapBackendToFrontend(updatedPontBascule);
      
      // Mettre à jour le cache local
      this.pontsBascule.set(id, mappedPontBascule);
      
      return mappedPontBascule;
    } catch (error) {
      console.error('Erreur mise à jour pont bascule:', error);
      throw error;
    }
  }

  /**
   * Créer un nouveau pont bascule
   */
  async createPontBascule(pontBascule: any): Promise<PontBascule> {
    // Mapper les données frontend vers backend
    const mappedData = this.mapFrontendToBackend(pontBascule);
    
    // Nettoyer les données avant envoi
    const cleanData = { ...mappedData };
    
    // Supprimer pontBasculeId vide lors de la création
    if (cleanData.scaleAgentConfig && cleanData.scaleAgentConfig.pontBasculeId === '') {
      (cleanData.scaleAgentConfig as any).pontBasculeId = undefined;
    }
  
    try {
      const response = await fetch(buildApiUrl('/pont-bascule/ponts-bascule/'), {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(cleanData),
      });

      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentification requise. Veuillez vous connecter.');
        }
        
        // Récupérer les détails de l'erreur
        let errorMessage = 'Erreur lors de la création du pont bascule';
        try {
          const errorData = await response.json();
          
          if (errorData.detail) {
            errorMessage = `Erreur 400: ${errorData.detail}`;
          } else if (errorData.message) {
            errorMessage = `Erreur 400: ${errorData.message}`;
          } else if (typeof errorData === 'string') {
            errorMessage = `Erreur 400: ${errorData}`;
          }
        } catch (parseError) {
          console.error('Impossible de parser la réponse d\'erreur:', parseError);
        }
        
        throw new Error(errorMessage);
      }

      const newPontBascule = await response.json();
      
      // Mapper les données du backend vers le frontend
      const mappedPontBascule = this.mapBackendToFrontend(newPontBascule);
      
      // Mettre à jour le cache local
      this.pontsBascule.set(mappedPontBascule.id, mappedPontBascule);
      
      return mappedPontBascule;
    } catch (error) {
      console.error('Erreur création pont bascule:', error);
      throw error;
    }
  }

  /**
   * Supprimer un pont bascule
   */
  async deletePontBascule(id: string): Promise<void> {
    try {
      const response = await fetch(buildApiUrl(`/pont-bascule/ponts-bascule/${id}/`), {
        method: 'DELETE',
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentification requise. Veuillez vous connecter.');
        }
        throw new Error('Erreur lors de la suppression du pont bascule');
      }
    } catch (error) {
      console.error('Erreur suppression pont bascule:', error);
      throw error;
    }
  }

  /**
   * Réinitialiser la clé secrète d'API d'un pont bascule
   */
  async resetApiSecretKey(pontBasculeId: string): Promise<{ new_key: string; warning: string }> {
    try {
      const response = await fetch(buildApiUrl(`/pont-bascule/ponts-bascule/${pontBasculeId}/reset_api_secret_key/`), {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentification requise. Veuillez vous connecter.');
        }
        throw new Error('Erreur lors de la réinitialisation de la clé secrète d\'API');
      }
      
      const data = await response.json();
      return {
        new_key: data.new_key,
        warning: data.warning
      };
    } catch (error) {
      console.error('Erreur réinitialisation clé secrète d\'API:', error);
      throw error;
    }
  }

  /**
   * Définir un pont bascule comme défaut pour un site
   */
  async setAsDefault(pontBasculeId: string): Promise<PontBascule> {
    try {
      const response = await fetch(buildApiUrl(`/pont-bascule/ponts-bascule/${pontBasculeId}/set_as_default/`), {
        method: 'POST',
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentification requise. Veuillez vous connecter.');
        }
        throw new Error('Erreur lors de la définition du pont bascule par défaut');
      }
      
      const pontBascule = await response.json();
      
      // Mettre à jour le cache local
      this.pontsBascule.set(pontBasculeId, pontBascule);
      
      return pontBascule;
    } catch (error) {
      console.error('Erreur définition pont bascule par défaut:', error);
      throw error;
    }
  }

  /**
   * Obtenir les statistiques d'un pont bascule
   */
  async getPontBasculeStatistics(pontBasculeId: string): Promise<any> {
    try {
      const response = await fetch(buildApiUrl(`/pont-bascule/ponts-bascule/${pontBasculeId}/statistics/`), {
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentification requise. Veuillez vous connecter.');
        }
        throw new Error('Erreur lors de la récupération des statistiques');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur récupération statistiques:', error);
      throw error;
    }
  }

  /**
   * Traiter un retour d'agent local
   */
  async processAgentReturn(pontBasculeId: string, agentData: any): Promise<any> {
    try {
      const response = await fetch(buildApiUrl(`/pont-bascule/ponts-bascule/${pontBasculeId}/process_agent_return/`), {
        method: 'POST',
        headers: this.getAuthHeaders(),
        body: JSON.stringify(agentData),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentification requise. Veuillez vous connecter.');
        }
        throw new Error('Erreur lors du traitement du retour d\'agent');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur traitement retour d\'agent:', error);
      throw error;
    }
  }

  /**
   * Obtenir l'historique des retours d'agent
   */
  async getAgentReturns(pontBasculeId: string, params?: any): Promise<any> {
    try {
      const queryParams = new URLSearchParams(params).toString();
      const url = `/pont-bascule/ponts-bascule/${pontBasculeId}/agent_returns/${queryParams ? `?${queryParams}` : ''}`;
      
      const response = await fetch(buildApiUrl(url), {
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentification requise. Veuillez vous connecter.');
        }
        throw new Error('Erreur lors de la récupération de l\'historique des retours d\'agent');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur récupération historique retours d\'agent:', error);
      throw error;
    }
  }

  /**
   * Obtenir le dernier retour d'agent
   */
  async getLatestAgentReturn(pontBasculeId: string): Promise<any> {
    try {
      const response = await fetch(buildApiUrl(`/pont-bascule/ponts-bascule/${pontBasculeId}/latest_agent_return/`), {
        headers: this.getAuthHeaders(),
      });
      
      if (!response.ok) {
        if (response.status === 401) {
          throw new Error('Authentification requise. Veuillez vous connecter.');
        }
        throw new Error('Erreur lors de la récupération du dernier retour d\'agent');
      }
      
      return await response.json();
    } catch (error) {
      console.error('Erreur récupération dernier retour d\'agent:', error);
      throw error;
    }
  }

  /**
   * Mettre à jour le cache local
   */
  private updateCache(pontsBascule: PontBascule[]): void {
    // Vider les caches existants
    this.pontsBascule.clear();
    this.sitePontsBascule.clear();

    // Remplir le cache principal
    pontsBascule.forEach(pont => {
      this.pontsBascule.set(pont.id, pont);
    });

    // Remplir le cache par site
    pontsBascule.forEach(pont => {
      const sitePonts = this.sitePontsBascule.get(pont.siteId) || [];
      sitePonts.push(pont);
      this.sitePontsBascule.set(pont.siteId, sitePonts);
    });
  }

  /**
   * Obtenir les ponts bascule en cache
   */
  getCachedPontsBascule(): PontBascule[] {
    return Array.from(this.pontsBascule.values());
  }

  /**
   * Rafraîchir les données d'un pont bascule spécifique
   * Utile pour maintenir la cohérence des données
   */
  async refreshPontBasculeData(pontId: string): Promise<PontBascule | null> {
    try {
      
      // Récupérer les données fraîches
      const freshData = await this.getPontBasculeById(pontId);
      
      if (freshData) {
        // Mettre à jour le cache
        this.pontsBascule.set(pontId, freshData);
        
        // Mettre à jour le cache par site
        const siteId = freshData.siteId;
        if (siteId) {
          const sitePonts = this.sitePontsBascule.get(siteId) || [];
          const existingIndex = sitePonts.findIndex(p => p.id === pontId);
          if (existingIndex >= 0) {
            sitePonts[existingIndex] = freshData;
          } else {
            sitePonts.push(freshData);
          }
          this.sitePontsBascule.set(siteId, sitePonts);
        }
      }
      
      return freshData;
    } catch (error) {
      console.error('Erreur lors du rafraîchissement des données du pont bascule:', error);
      return null;
    }
  }

  /**
   * Obtenir les ponts bascule d'un site en cache
   */
  getCachedPontsBasculeBySite(siteId: string): PontBascule[] {
    return this.sitePontsBascule.get(siteId) || [];
  }

  /**
   * Vider le cache
   */
  clearCache(): void {
    this.pontsBascule.clear();
    this.sitePontsBascule.clear();
  }

  /**
   * Mapper les données du frontend vers le format attendu par le backend
   */
  private mapFrontendToBackend(frontendData: any): any {
    const mappedData: any = {};
    
    // Mapping des champs de base (avec validation)
    if (frontendData.name !== undefined) mappedData.name = frontendData.name;
    
    // Validation et mapping du statut (seulement les valeurs acceptées par le backend)
    if (frontendData.status !== undefined) {
      const validStatuses = ['active', 'inactive', 'maintenance'];
      if (validStatuses.includes(frontendData.status)) {
        mappedData.status = frontendData.status;
      } else {
        console.warn('⚠️ Statut invalide ignoré:', frontendData.status, '-> valeurs valides:', validStatuses);
      }
    }
    
    if (frontendData.location !== undefined) mappedData.location = frontendData.location;
    if (frontendData.description !== undefined) mappedData.description = frontendData.description;
    if (frontendData.isDefault !== undefined) mappedData.is_default = frontendData.isDefault;
    
    // Mapping des IDs (seulement si présents et valides)
    if (frontendData.tenantId !== undefined) {
      const tenantId = parseInt(frontendData.tenantId.toString(), 10);
      if (!isNaN(tenantId)) {
        mappedData.tenant = tenantId; // Le backend attend 'tenant', pas 'tenant_id'
      }
    }
    
    if (frontendData.siteId !== undefined) {
      const siteId = parseInt(frontendData.siteId.toString(), 10);
      if (!isNaN(siteId)) {
        mappedData.site = siteId; // Le backend attend 'site', pas 'site_id'
      }
    }
    
    // Mapping des configurations Scale Agent (selon le modèle backend)
    if (frontendData.scaleAgentConfig !== undefined) {
      const config = frontendData.scaleAgentConfig;
      
      // Validation de l'URL WebSocket
      if (config.wsUrl !== undefined) {
        // Maintenant que le backend accepte les URLs WebSocket avec CharField
        mappedData.ws_url = config.wsUrl;
      }
      
      if (config.port !== undefined) mappedData.port = config.port;
      if (config.scale?.unit !== undefined) mappedData.scale_unit = config.scale.unit;
      if (config.scale?.precision !== undefined) mappedData.scale_precision = config.scale.precision;
      if (config.scale?.stabilityThreshold !== undefined) mappedData.stability_threshold = config.scale.stabilityThreshold;
      if (config.reconnect?.enabled !== undefined) mappedData.reconnect_enabled = config.reconnect.enabled;
      if (config.reconnect?.maxAttempts !== undefined) mappedData.reconnect_max_attempts = config.reconnect.maxAttempts;
      if (config.reconnect?.delay !== undefined) mappedData.reconnect_delay = config.reconnect.delay;
    }
    
    // Mapping des configurations réseau (selon le modèle backend)
    if (frontendData.networkConfig !== undefined) {
      const config = frontendData.networkConfig;
      if (config.ipAddress !== undefined) mappedData.network_ip_address = config.ipAddress;
      if (config.subnet !== undefined) mappedData.network_subnet = config.subnet;
      if (config.gateway !== undefined) mappedData.network_gateway = config.gateway;
      if (config.dns !== undefined) mappedData.network_dns = config.dns;
      
      // Mapping des informations d'agent (si présentes)
      if (config.localIp !== undefined) mappedData.agent_local_ip = config.localIp;
      if (config.macAddress !== undefined) mappedData.agent_mac_address = config.macAddress;
      if (config.hostname !== undefined) mappedData.agent_hostname = config.hostname;
      if (config.osInfo !== undefined) mappedData.agent_os_info = config.osInfo;
    }
    
    // Mapping des configurations de sécurité (selon le modèle backend)
    if (frontendData.securityConfig !== undefined) {
      const config = frontendData.securityConfig;
      if (config.apiKey !== undefined) mappedData.api_key = config.apiKey;
      if (config.allowedOrigins !== undefined) mappedData.allowed_origins = config.allowedOrigins;
      if (config.requireAuthentication !== undefined) mappedData.require_authentication = config.requireAuthentication;
    }
    
    // Mapping direct des champs d'agent (pour les fichiers de retour)
    if (frontendData.agentLocalIp !== undefined) mappedData.agent_local_ip = frontendData.agentLocalIp;
    if (frontendData.agentMacAddress !== undefined) mappedData.agent_mac_address = frontendData.agentMacAddress;
    if (frontendData.agentHostname !== undefined) mappedData.agent_hostname = frontendData.agentHostname;
    if (frontendData.agentOsInfo !== undefined) mappedData.agent_os_info = frontendData.agentOsInfo;
    
    // Mapping des champs directs (pour compatibilité avec l'ancienne structure)
    if (frontendData.wsUrl !== undefined) mappedData.ws_url = frontendData.wsUrl;
    if (frontendData.port !== undefined) mappedData.port = frontendData.port;
    if (frontendData.scaleUnit !== undefined) mappedData.scale_unit = frontendData.scaleUnit;
    if (frontendData.scalePrecision !== undefined) mappedData.scale_precision = frontendData.scalePrecision;
    if (frontendData.stabilityThreshold !== undefined) mappedData.stability_threshold = frontendData.stabilityThreshold;
    if (frontendData.reconnectEnabled !== undefined) mappedData.reconnect_enabled = frontendData.reconnectEnabled;
    if (frontendData.reconnectMaxAttempts !== undefined) mappedData.reconnect_max_attempts = frontendData.reconnectMaxAttempts;
    if (frontendData.reconnectDelay !== undefined) mappedData.reconnect_delay = frontendData.reconnectDelay;
    if (frontendData.networkIpAddress !== undefined) mappedData.network_ip_address = frontendData.networkIpAddress;
    if (frontendData.networkSubnet !== undefined) mappedData.network_subnet = frontendData.networkSubnet;
    if (frontendData.networkGateway !== undefined) mappedData.network_gateway = frontendData.networkGateway;
    if (frontendData.networkDns !== undefined) mappedData.network_dns = frontendData.networkDns;
    if (frontendData.apiKey !== undefined) mappedData.api_key = frontendData.apiKey;
    if (frontendData.allowedOrigins !== undefined) mappedData.allowed_origins = frontendData.allowedOrigins;
    if (frontendData.requireAuthentication !== undefined) mappedData.require_authentication = frontendData.requireAuthentication;
    
    
    return mappedData;
  }
  
  /**
   * Mapper les données du backend vers le format attendu par le frontend
   */
  private mapBackendToFrontend(backendData: any): PontBascule {
    const mappedData: any = {
      // Champs de base
      id: backendData.id,
      code: backendData.code,
      name: backendData.name,
      tenantId: backendData.tenant_id || backendData.tenant,
      siteId: backendData.site_id || backendData.site,
      location: backendData.location,
      description: backendData.description,
      is_default: backendData.is_default,
      status: backendData.status,
      
      // Informations du tenant (depuis le backend)
      tenant_id: backendData.tenant_id || backendData.tenant,
      tenant_name: backendData.tenant_name,
      tenant_code: backendData.tenant_code,
      
      // Informations du site (depuis le backend)
      site_id: backendData.site_id || backendData.site,
      site_name: backendData.site_name,
      site_code: backendData.site_code,
      
      // Configuration Scale Agent
      wsUrl: backendData.ws_url || backendData.websocket_url,
      port: backendData.port,
      scaleUnit: backendData.scale_unit,
      scalePrecision: backendData.scale_precision,
      stabilityThreshold: backendData.stability_threshold,
      
      // Configuration de reconnexion
      reconnectEnabled: backendData.reconnect_enabled,
      reconnectMaxAttempts: backendData.reconnect_max_attempts,
      reconnectDelay: backendData.reconnect_delay,
      
      // Configuration réseau
      networkIpAddress: backendData.network_ip_address,
      networkSubnet: backendData.network_subnet,
      networkGateway: backendData.network_gateway,
      networkDns: backendData.network_dns,
      
      // Informations de l'agent local
      agentLocalIp: backendData.agent_local_ip,
      agentMacAddress: backendData.agent_mac_address,
      agentHostname: backendData.agent_hostname,
      agentOsInfo: backendData.agent_platform,
      
      // Statuts de l'agent
      agentInstallationStatus: backendData.agent_installation_status,
      agentNetworkStatus: backendData.agent_network_status,
      agentServiceStatus: backendData.agent_service_status,
      
      // Configuration du service de l'agent
      agentServiceName: backendData.agent_service_name,
      agentServiceDisplayName: backendData.agent_service_display_name,
      agentServiceStartupType: backendData.agent_service_startup_type,
      
      // Informations de sécurité de l'agent
      agentApiKey: backendData.agent_api_key,
      agentKeyGeneratedAt: backendData.agent_key_generated_at,
      agentKeyLength: backendData.agent_key_length,
      
      // Informations de la balance de l'agent
      agentBalanceConnected: backendData.agent_balance_connected,
      agentBalanceModel: backendData.agent_balance_model,
      agentBalanceSerialNumber: backendData.agent_balance_serial_number,
      agentBalanceCalibrationDate: backendData.agent_balance_calibration_date,
      agentBalanceNextCalibration: backendData.agent_balance_next_calibration,
      agentBalancePort: backendData.agent_balance_port,
      agentBalanceBaudRate: backendData.agent_balance_baud_rate,
      
      // Informations de communication de l'agent
      agentLastCommunication: backendData.agent_last_communication,
      agentApiEndpoint: backendData.agent_api_endpoint,
      agentWebsocketEndpoint: backendData.agent_websocket_endpoint,
      
      // Endpoints de l'agent
      agentHealthEndpoint: backendData.agent_health_endpoint,
      agentWeighReadEndpoint: backendData.agent_weigh_read_endpoint,
      agentWeighStreamEndpoint: backendData.agent_weigh_stream_endpoint,
      
      // Version de l'agent
      agentVersion: backendData.agent_version,
      
      // Informations de monitoring de l'agent
      agentLogLevel: backendData.agent_log_level,
      agentHeartbeatInterval: backendData.agent_heartbeat_interval,
      agentMaxRetries: backendData.agent_max_retries,
      agentSyncInterval: backendData.agent_sync_interval,
      
      // Configuration de sécurité
      apiKey: backendData.api_key,
      apiSecretKey: backendData.api_secret_key,
      allowedOrigins: backendData.allowed_origins || [],
      requireAuthentication: backendData.require_authentication,
      
      // Calibration
      lastCalibration: backendData.last_calibration,
      nextCalibration: backendData.next_calibration,
      
      // Audit
      createdAt: backendData.created_at,
      updatedAt: backendData.updated_at,
      createdBy: backendData.created_by,
      updatedBy: backendData.updated_by,
      
      // Champs additionnels détectés par l'agent
      agentInstallDate: backendData.agent_install_date,
      agentInstallerVersion: backendData.agent_installer_version,
      agentDetectedLocalIp: backendData.agent_detected_local_ip,
      agentDetectedGateway: backendData.agent_detected_gateway,
      agentDetectedDns: backendData.agent_detected_dns,
      agentDetectedSubnetMask: backendData.agent_detected_subnet_mask,
      agentConfiguredLocalIp: backendData.agent_configured_local_ip,
      agentConfiguredGateway: backendData.agent_configured_gateway,
      agentConfiguredDns: backendData.agent_configured_dns,
      agentConfiguredSubnetMask: backendData.agent_configured_subnet_mask,
      
      // Configuration Scale Agent (compatibilité)
      scaleAgentConfig: {
        port: backendData.port,
        scale: {
          unit: backendData.scale_unit,
          precision: backendData.scale_precision,
          stabilityThreshold: backendData.stability_threshold,
        },
        reconnect: {
          enabled: backendData.reconnect_enabled,
          maxAttempts: backendData.reconnect_max_attempts,
          delay: backendData.reconnect_delay,
        },
      },
      
      // Configuration réseau (compatibilité)
      networkConfig: {
        ipAddress: backendData.network_ip_address,
        subnet: backendData.network_subnet,
        gateway: backendData.network_gateway,
        dns: backendData.network_dns,
        localIp: backendData.agent_local_ip,
        macAddress: backendData.agent_mac_address,
        hostname: backendData.agent_hostname,
        osInfo: backendData.agent_platform,
      },
      
      // Configuration sécurité (compatibilité)
      securityConfig: {
        apiKey: backendData.api_key,
        requireAuthentication: backendData.require_authentication,
        allowedOrigins: backendData.allowed_origins || [],
      },
    };
    
    return mappedData;
  }

  // Méthode isValidUrl supprimée car non utilisée

  /**
   * Générer une configuration par défaut pour un Scale Agent
   */
  generateDefaultScaleAgentConfig(
    pontBasculeName: string,
    siteId: string,
    pontBasculeId: string
  ): ScaleAgentConfig {
    return {
              wsUrl: `ws://127.0.0.1:9091/weigh/stream`,
      port: 9090,
      pontBasculeName,
      siteId,
      pontBasculeId,
      reconnect: {
        enabled: true,
        maxAttempts: 5,
        delay: 1000,
      },
      scale: {
        unit: 'kg',
        precision: 2,
        stabilityThreshold: 0.01,
      },
    };
  }

  /**
   * Valider une configuration de Scale Agent
   */
  validateScaleAgentConfig(config: ScaleAgentConfig): { valid: boolean; errors: string[] } {
    const errors: string[] = [];

    if (!config.wsUrl) {
      errors.push('URL WebSocket requise');
    }

    if (!config.port || config.port < 1 || config.port > 65535) {
      errors.push('Port invalide (1-65535)');
    }

    if (!config.pontBasculeName) {
      errors.push('Nom du pont bascule requis');
    }

    if (!config.siteId) {
      errors.push('ID du site requis');
    }

    if (!config.pontBasculeId) {
      errors.push('ID du pont bascule requis');
    }

    if (config.reconnect.maxAttempts < 0) {
      errors.push('Nombre de tentatives de reconnexion invalide');
    }

    if (config.reconnect.delay < 100) {
      errors.push('Délai de reconnexion trop court (minimum 100ms)');
    }

    if (config.scale.precision < 0 || config.scale.precision > 6) {
      errors.push('Précision invalide (0-6 décimales)');
    }

    if (config.scale.stabilityThreshold <= 0) {
      errors.push('Seuil de stabilité invalide');
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }
}

// Instance singleton
export const pontBasculeService = PontBasculeService.getInstance();
